// ─── YouTube video card with oEmbed preview ─────────────────────────────────
// Paste a URL → fetches title, channel, thumbnail from YouTube's oEmbed API.
// Preview card is clickable to open the video. Expiration date field alongside.

import { useState, useEffect } from 'react';
import { Trash2 } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import type { MediaFileItem } from './types';

// Re-export social shared components used by the form renderer
import {
  FieldLabel as SocialFieldLabel,
  inputClass as socialInputClass,
} from '../../ad-details/shared';

interface YouTubeOEmbed {
  title: string;
  author_name: string;
  thumbnail_url: string;
}

export function isYouTubeUrl(url: string): boolean {
  return /^https?:\/\/(www\.)?(youtube\.com|youtu\.be)\/.+/.test(url);
}

function useYouTubeOEmbed(url: string | undefined) {
  const [data, setData] = useState<YouTubeOEmbed | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!url || !isYouTubeUrl(url)) { setData(null); return; }
    let cancelled = false;
    setLoading(true);
    fetch(`https://www.youtube.com/oembed?url=${encodeURIComponent(url)}&format=json`)
      .then(res => { if (!res.ok) throw new Error('Not found'); return res.json(); })
      .then((json: YouTubeOEmbed) => { if (!cancelled) setData(json); })
      .catch(() => { if (!cancelled) setData(null); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [url]);

  return { data, loading };
}

export function YouTubeVideoCard({
  item, index, onUrlChange, onExpirationChange, onRemove, canRemove = true, issue, expirationIssue,
}: {
  item: MediaFileItem;
  index: number;
  onUrlChange: (url: string) => void;
  onExpirationChange: (date: string) => void;
  canRemove?: boolean;
  onRemove: () => void;
  issue?: { errors: string[]; warnings: string[] };
  expirationIssue?: { errors: string[]; warnings: string[] };
}) {
  const { data: oembed, loading } = useYouTubeOEmbed(item.url);

  return (
    <div className={`rounded-lg border p-3 ${issue?.warnings.length ? 'border-amber-300 bg-amber-50/30' : 'border-slate-200 bg-slate-50/50'}`}>
      <div className="flex items-start justify-between mb-2">
        <p className="text-[11px] font-semibold text-slate-500">Video {index + 1}</p>
        <button
          type="button" onClick={onRemove} disabled={!canRemove}
          className="w-6 h-6 flex items-center justify-center rounded-md text-slate-400 hover:text-red-500 hover:bg-red-50 transition-colors disabled:opacity-20 disabled:hover:text-slate-400 disabled:hover:bg-transparent"
        >
          <Trash2 className="w-3.5 h-3.5" />
        </button>
      </div>

      <div className="flex gap-3">
        <div className="flex-1 min-w-0">
          <SocialFieldLabel label="YouTube URL" />
          <input type="url" value={item.url ?? ''} onChange={e => onUrlChange(e.target.value)} placeholder="https://youtube.com/watch?v=..." className={`${socialInputClass} ${issue?.errors.length ? 'border-2 !border-red-400' : ''}`} />
        </div>
        <div className="w-36 flex-shrink-0">
          <SocialFieldLabel label="Expiration Date" />
          <input type="date" value={item.expirationDate ?? ''} onChange={e => onExpirationChange(e.target.value)} className={`${socialInputClass} ${expirationIssue?.errors.length ? 'border-2 !border-red-400' : ''} ${item.expirationDate ? 'text-slate-900' : 'text-slate-400'}`} />
        </div>
      </div>

      {loading && (
        <div className="mt-2 flex items-center gap-2 text-slate-400">
          <div className="w-3 h-3 border border-slate-300 border-t-slate-500 rounded-full animate-spin" />
          <span className="text-[11px]">Loading preview...</span>
        </div>
      )}
      {oembed && !loading && (
        <a href={item.url} target="_blank" rel="noopener noreferrer" className="mt-2 flex gap-3 rounded-lg border border-slate-200 bg-white p-2 hover:border-slate-300 hover:shadow-sm transition-all cursor-pointer">
          <img src={oembed.thumbnail_url} alt={oembed.title} className="w-24 h-[54px] object-cover rounded flex-shrink-0" />
          <div className="min-w-0 flex flex-col justify-center">
            <Tooltip>
              <TooltipTrigger asChild>
                <p className="text-xs font-medium text-slate-700 truncate cursor-default">{oembed.title}</p>
              </TooltipTrigger>
              <TooltipContent side="top" className="max-w-72">{oembed.title}</TooltipContent>
            </Tooltip>
            <p className="text-[11px] text-slate-400 truncate">{oembed.author_name}</p>
          </div>
        </a>
      )}
      {item.url && isYouTubeUrl(item.url) && !oembed && !loading && (
        <p className="mt-2 text-[11px] text-amber-500">Could not load video preview</p>
      )}
      {issue?.errors.map((msg, i) => (
        <p key={`e${i}`} className="mt-2 text-xs text-red-600">✕ {msg}</p>
      ))}
      {expirationIssue?.errors.map((msg, i) => (
        <p key={`exp${i}`} className="mt-2 text-xs text-red-600">✕ {msg}</p>
      ))}
      {issue?.warnings.map((msg, i) => (
        <p key={`w${i}`} className="mt-2 text-xs text-amber-600">⚠ {msg}</p>
      ))}
    </div>
  );
}
